import { resolveServiceTypeDisplayLabel } from '../../../utils/serviceTypeDisplay';
import { getTradeDisplayName } from '../../../constants/mockData/allTrades';

const SITE_NAME = 'NKY Connect';
const TITLE_SUFFIX = ` | ${SITE_NAME}`;
const MAX_DESCRIPTION_LENGTH = 155;

const TITLE_CASE_ACRONYMS = new Set(['nky', 'hvac', 'sms', 'ai', 'og', 'url']);

/**
 * Capitalize each word for page titles (preserves known acronyms like HVAC, NKY).
 * @param {string} text
 * @returns {string}
 */
export function toTitleCaseWords(text) {
  const raw = typeof text === 'string' ? text.trim() : '';
  if (!raw) return '';

  return raw
    .split(/\s+/)
    .map((word) => {
      if (word === '&') return word;
      if (/^[A-Z0-9]{2,}$/.test(word)) return word;
      return word
        .split('-')
        .map((part) => {
          if (!part) return part;
          if (/^[A-Z0-9]{2,}$/.test(part)) return part;
          const lower = part.toLowerCase();
          if (TITLE_CASE_ACRONYMS.has(lower)) return lower.toUpperCase();
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('-');
    })
    .join(' ');
}

/**
 * Title-case the page title and ensure it ends with "| NKY Connect".
 * @param {string} [segment]
 * @returns {string}
 */
export function formatPageTitle(segment) {
  const raw = typeof segment === 'string' ? segment.trim() : '';
  if (!raw || raw === SITE_NAME) return SITE_NAME;

  let core = raw;
  const suffixIdx = raw.indexOf('| NKY Connect');
  if (suffixIdx !== -1) {
    core = raw.slice(0, suffixIdx).trim();
  }

  const cased = toTitleCaseWords(core);
  if (!cased) return SITE_NAME;
  return `${cased}${TITLE_SUFFIX}`;
}

/**
 * @param {string | undefined | null} configuredSiteUrl
 * @returns {string}
 */
export function resolveSiteOrigin(configuredSiteUrl) {
  const fromEnv =
    typeof configuredSiteUrl === 'string' ? configuredSiteUrl.trim().replace(/\/$/, '') : '';
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://nkyconnect.com';
}

/**
 * Build an absolute URL from origin + path or pass through absolute URLs.
 * @param {string} origin
 * @param {string} [pathOrUrl]
 * @returns {string}
 */
export function resolveAbsoluteUrl(origin, pathOrUrl) {
  const value = typeof pathOrUrl === 'string' ? pathOrUrl.trim() : '';
  if (!value) return origin;
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${origin.replace(/\/$/, '')}${path}`;
}

/**
 * Truncate for meta description length.
 * @param {string} [text]
 * @param {number} [max]
 * @returns {string}
 */
export function truncateDescription(text, max = MAX_DESCRIPTION_LENGTH) {
  const raw = typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : '';
  if (!raw) return '';
  if (raw.length <= max) return raw;
  const sliced = raw.slice(0, max - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  const cut = lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced;
  return `${cut}…`;
}

/**
 * Rank business trades by SubServices count (catalog first, then `other` if none).
 * @param {Array<{ ServiceType?: string, SubServices?: unknown[] }> | undefined} services
 * @param {number} [limit]
 * @returns {string[]} Display labels, highest count first
 */
export function getRankedTradeLabels(services, limit = 3) {
  if (!Array.isArray(services) || services.length === 0) return [];

  /** @type {{ slug: string, count: number }[]} */
  const scored = [];
  for (const entry of services) {
    const slug = typeof entry?.ServiceType === 'string' ? entry.ServiceType.trim() : '';
    if (!slug) continue;
    const count = Array.isArray(entry.SubServices) ? entry.SubServices.length : 0;
    scored.push({ slug, count });
  }
  if (scored.length === 0) return [];

  const byCountDesc = (a, b) => b.count - a.count;
  const catalog = scored.filter((s) => s.slug !== 'other').sort(byCountDesc);
  const ranked = (catalog.length > 0 ? catalog : scored.sort(byCountDesc)).slice(
    0,
    Math.max(0, limit),
  );

  const labels = [];
  for (const { slug } of ranked) {
    const label = resolveServiceTypeDisplayLabel(slug);
    if (label && label !== '—') labels.push(label);
  }
  return labels;
}

/**
 * Trade label with the most registered subservices (catalog trades preferred over `other`).
 * @param {Array<{ ServiceType?: string, SubServices?: unknown[] }> | undefined} services
 * @returns {string}
 */
export function getPrimaryTradeLabel(services) {
  return getRankedTradeLabels(services, 1)[0] || '';
}

/**
 * Natural English join for trade names in meta descriptions.
 * @param {string[]} labels
 * @returns {string}
 */
export function formatTradeListPhrase(labels) {
  const parts = (Array.isArray(labels) ? labels : [])
    .map((l) => (typeof l === 'string' ? l.trim().toLowerCase() : ''))
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * @param {object} opts
 * @param {object} [opts.business]
 * @param {boolean} [opts.notFound]
 * @returns {{ title: string, description: string, path: string, image?: string, noindex?: boolean, type?: string }}
 */
export function buildStorefrontSeo({ business, notFound = false } = {}) {
  if (notFound || !business) {
    return {
      title: 'Business not found',
      description: 'This business listing could not be found on NKY Connect.',
      path: typeof window !== 'undefined' ? window.location.pathname : '/storefront',
      noindex: true,
    };
  }

  const name =
    (typeof business.BusinessName === 'string' && business.BusinessName.trim()) ||
    'Local business';
  const city =
    (typeof business.BusinessAddress?.City === 'string' &&
      business.BusinessAddress.City.trim()) ||
    '';
  const rankedTrades = getRankedTradeLabels(business.Services, 3);
  const tradePhrase = formatTradeListPhrase(rankedTrades);
  const state =
    (typeof business.BusinessAddress?.State === 'string' &&
      business.BusinessAddress.State.trim()) ||
    'KY';

  const place = city ? `${city}, ${state}` : 'Northern Kentucky';
  const tradePlaceLead = tradePhrase
    ? `${name} — ${tradePhrase} in ${place}`
    : `${name} — local home services in ${place}`;

  const aboutRaw =
    typeof business.BusinessAbout === 'string' ? business.BusinessAbout.trim() : '';
  const description = truncateDescription(
    aboutRaw
      ? `${tradePlaceLead}. ${aboutRaw}`
      : `${tradePlaceLead}. Hire trusted local pros on NKY Connect.`,
  );

  const slug =
    (typeof business.BusinessSlug === 'string' && business.BusinessSlug.trim()) ||
    '';
  const path = slug ? `/storefront/${slug}` : '/storefront';

  const photo =
    typeof business.BusinessPhotoUrl === 'string'
      ? business.BusinessPhotoUrl.trim()
      : '';
  const image = /^https?:\/\//i.test(photo) ? photo : undefined;

  return {
    title: name,
    description,
    path,
    image,
    type: 'profile',
  };
}

/**
 * SEO props for a catalog trade entry page.
 * @param {string} tradeId
 * @returns {{ title: string, description: string, path: string }}
 */
export function buildTradeSeo(tradeId) {
  const label = getTradeDisplayName(tradeId) || tradeId;
  return {
    title: `${label} services in Northern Kentucky`,
    description: `Find trusted ${label.toLowerCase()} professionals in Northern Kentucky. Tell us what you need and connect with local providers on NKY Connect.`,
    path: `/${tradeId}`,
  };
}

/**
 * SEO props for the service search entry (`/find-a-pro`).
 * @returns {{ title: string, description: string, path: string }}
 */
export function buildServiceSearchSeo() {
  return {
    title: 'Find a local pro in Northern Kentucky',
    description:
      'Tell us what you need and see matched plumbers, electricians, HVAC pros, and landscapers in Boone, Kenton, and Campbell counties. No sign-up needed.',
    path: '/find-a-pro',
  };
}

/**
 * SEO props for trade provider search results.
 * @param {string} tradeId
 * @param {string | null} [taskLabel] catalog sub-service label when the search picked one
 * @returns {{ title: string, description: string, path: string }}
 */
export function buildTradeProvidersSeo(tradeId, taskLabel) {
  const label = getTradeDisplayName(tradeId) || tradeId;
  const task = typeof taskLabel === 'string' ? taskLabel.trim() : '';
  const title = task
    ? `${task} pros in Northern Kentucky`
    : `${label} providers in Northern Kentucky`;
  const description = task
    ? `Compare local ${label.toLowerCase()} pros for ${task.toLowerCase()} in Northern Kentucky: ratings, prices, and availability on NKY Connect.`
    : `Browse ${label.toLowerCase()} service providers in Northern Kentucky on NKY Connect.`;
  return {
    title,
    description,
    path: `/${tradeId}/providers`,
  };
}

const PRIVATE_EXACT = new Map([
  ['/login', 'Log in'],
  ['/sign-up', 'Sign up'],
  ['/provider-signup', 'Provider signup'],
  ['/join-existing-business', 'Join a business'],
  ['/create-business', 'Create a business'],
  ['/verify-email', 'Verify email'],
  ['/forgot-password', 'Forgot password'],
  ['/reset-password', 'Reset password'],
  ['/account', 'Account'],
  ['/account/change-password', 'Change password'],
  ['/dashboard', 'Business dashboard'],
  ['/dashboard/partnership-agreement', 'Partnership agreement'],
  ['/dashboard/service-catalog', 'Service catalog'],
  ['/dashboard/service-requests', 'Service Requests'],
  ['/dashboard/sms-notifications', 'SMS notifications'],
  ['/admin', 'Admin'],
  ['/payment-methods', 'Payment methods'],
  ['/add-payment-method', 'Add payment method'],
  ['/invoices', 'Invoices'],
  ['/ai-service-search', 'AI service search'],
  ['/ai-service-recommendations', 'AI recommendations'],
]);

const PRIVATE_PREFIXES = ['/dashboard', '/account'];

/**
 * @param {string} pathname
 * @returns {{ title: string, noindex: true, path: string } | null}
 */
export function getPrivatePageSeo(pathname) {
  const path = typeof pathname === 'string' ? pathname : '';
  if (!path) return null;

  if (PRIVATE_EXACT.has(path)) {
    return {
      title: PRIVATE_EXACT.get(path),
      noindex: true,
      path,
    };
  }

  for (const prefix of PRIVATE_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return {
        title: PRIVATE_EXACT.get(prefix) || 'Account',
        noindex: true,
        path,
      };
    }
  }

  return null;
}
